import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Megaphone, Landmark, Compass } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_META = {
  bill:         { icon: Landmark,  label: "Bill" },
  action_alert: { icon: Megaphone, label: "Action" },
  statement:    { icon: FileText,  label: "Statement" },
  priority:     { icon: Compass,   label: "Priority" },
};

/**
 * Global site search — used inside the public Header.
 * Debounced 200ms, closes on outside click / Escape.
 */
export default function HeaderSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/search", { params: { q } });
        setResults(data.results || []); setActive(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const go = (r) => {
    setOpen(false); setQ(""); setResults([]);
    navigate(r.url);
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="header-search">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"/>
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search bills, statements..."
          data-testid="header-search-input"
          className="w-56 lg:w-72 pl-9 pr-8 py-2 text-sm border border-stone-300 focus:outline-none focus:border-ccc-purple bg-white"
        />
        {q && (
          <button
            onClick={() => { setQ(""); setResults([]); setOpen(false); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-ccc-plum"
            aria-label="Clear"
          >
            <X size={14}/>
          </button>
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-300 shadow-lg max-h-96 overflow-y-auto z-50" data-testid="search-results">
          {loading && <div className="p-4 text-sm text-stone-500">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-4 text-sm text-stone-500">No results for "{q}".</div>
          )}
          {results.map((r, i) => {
            const meta = TYPE_META[r.type] || { icon: FileText, label: r.type };
            const Icon = meta.icon;
            return (
              <button
                key={r.id + r.type}
                onClick={() => go(r)}
                onMouseEnter={() => setActive(i)}
                data-testid={`search-result-${r.type}-${r.id}`}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 last:border-0 flex items-start gap-3 ${
                  i === active ? "bg-ccc-plum text-ccc-ivory" : "hover:bg-stone-50"
                }`}
              >
                <Icon size={16} className={`mt-0.5 shrink-0 ${i === active ? "text-ccc-gold" : "text-ccc-purple"}`}/>
                <div className="min-w-0 flex-1">
                  <div className={`text-[10px] uppercase tracking-wider ${i === active ? "text-ccc-gold" : "text-ccc-gold-dark"}`}>
                    {meta.label}
                  </div>
                  <div className={`font-serif text-sm leading-tight truncate ${i === active ? "text-ccc-ivory" : "text-ccc-plum"}`}>
                    {r.title}
                  </div>
                  <div className={`text-xs ${i === active ? "text-stone-300" : "text-stone-500"}`}>{r.subtitle}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
